import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { neutral } from "app/themes/colors";
import { DIALER_CALL_STATE_ON_HOLD } from "components/suno-dialer/constants";
import useSunoDialer from "components/suno-dialer/use-suno-dialer.hook";
import { dateDifferenceText } from "components/suno-dialer/utils";
import { CRM_SEGMENTS } from "constants/patients";
import { formatPhoneNumber } from "utils/common";

type HeaderBlockProps = {
  elapsedTime: string;
};

export default function HeaderBlock({ elapsedTime }: HeaderBlockProps) {
  const theme = useTheme();

  const {
    activeCall,
    activeCallDetails,
    isExpanded,
    handleToggleExpand,
    patient
  } = useSunoDialer();
  const { extra } = activeCallDetails || {};

  const patientAddedText = patient?.created_at_display
    ? dateDifferenceText(patient?.created_at_display)
    : "";

  const isMuted = activeCall?.isMuted;

  const isOnHold = activeCallDetails?.state === DIALER_CALL_STATE_ON_HOLD;

  const displayName = patient?.fullName
    ? patient?.fullName
    : extra?.phone_number
      ? formatPhoneNumber(String(extra?.phone_number))
      : "Phone number is hidden";

  return (
    <Box sx={{ px: 0, pt: 0, pb: 1, cursor: isExpanded ? "grab" : "pointer" }}>
      <Stack
        spacing={1.5}
        sx={{
          color: "white",
          background: isOnHold
            ? neutral[800]
            : isMuted
              ? "hsla(180, 1%, 15%, 0.8)"
              : "hsla(154, 45%, 40%, 1)",
          px: 2,
          pt: 1,
          pb: 1,
          borderRadius: "8px",
          boxShadow: theme.shadows[3]
        }}
        onClick={(event) => {
          event.stopPropagation();
          if (!isExpanded) {
            handleToggleExpand();
          }
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-between"
          width="100%"
          alignItems="center"
        >
          <Stack width="100%" height="100%" spacing={0.25} pr={2}>
            <Typography
              sx={{
                fontSize: "17px",
                fontWeight: 700,
                lineHeight: 1.3
              }}
            >
              Active Call {isOnHold ? "is On Hold" : ""}
              {!isOnHold && isMuted ? "(Muted)" : ""}{" "}
            </Typography>

            {!isExpanded && (
              <Typography
                sx={{
                  fontSize: "15px",
                  lineHeight: 1.3
                }}
              >
                {displayName}
                {patient?.crm_segment
                  ? ` (${CRM_SEGMENTS.getFromCode(patient?.crm_segment)})`
                  : null}
              </Typography>
            )}

            {isExpanded &&
              Boolean(patient?.crm_segment || patientAddedText) && (
                <Typography
                  sx={{
                    fontSize: "15px",
                    lineHeight: 1.3
                  }}
                >
                  {CRM_SEGMENTS.getFromCode(patient?.crm_segment)}
                  {patientAddedText ? ", " : ""}
                  {Boolean(patientAddedText) && (
                    <Box component="span" sx={{ fontSize: "13px", pl: 0.5 }}>
                      patient added {patientAddedText}
                    </Box>
                  )}
                </Typography>
              )}
          </Stack>
          <Stack maxWidth="80px" sx={{ mr: 1.5 }}>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                lineHeight: 1.3
              }}
            >
              {elapsedTime}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack maxWidth="80px" sx={{ position: "absolute", right: 2, top: 2 }}>
        <IconButton
          size="small"
          sx={{
            p: 0,
            transform: "scale(0.9)",
            background: "hsla(180, 1%, 15%, 0.3)",

            color: "white"
          }}
          onClick={(event) => {
            event.stopPropagation();
            handleToggleExpand();
          }}
        >
          {isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      </Stack>
    </Box>
  );
}
